import React, { useState, useEffect, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Activity, AlertTriangle, ArrowLeft, Bell, BellOff, Brain, Info, Landmark, Layers, PencilLine, SlidersHorizontal, Sparkles, Target, Zap } from 'lucide-react';
import { convertOptionsSymbol, formatNumber, formatPremium, getBullflowColorPrefCB, optionColorShouldBeGreenOnlyType, parseOptionInfo, timeAgo } from '../Utils';
import { collection, query, onSnapshot, orderBy, limit, where } from "firebase/firestore";
import { firestore } from '../firebase';
import { Tooltip } from '@material-ui/core';
import alertSound from '../alert_sound_effect2.mp3';
import CustomCircleIconButton from './CustomCircleIconButton';
import AlertsListView from '../AlertsListView';
import DiscordIcon from '../images/discord_icon_blue.png';
import { useAsyncPerformance } from '../MainDashboardCellRendering';

// Define your checkbox topics here
const CHECKBOX_TOPICS = ["urgent", "bullflow", "position_builder", "custom", "discord", "grenade", "sizable"];
const MAX_STORED_ALERTS = 400;      // <— hard cap for hidden-tab buffering
const bullflowColorPref = getBullflowColorPrefCB();
const BULLFLOW_GREEN = "#20d46b";
const BULLFLOW_RED = "#cc3753";
const BULLFLOW_OPACITY_GREEN = "rgba(2, 212, 44, 0.15)";
const BULLFLOW_OPACITY_RED = "rgba(250, 0, 67, 0.15)";
const CB_OPACITY_GREEN = "rgba(44, 140, 90, 0.15)";
const CB_OPACITY_RED = "rgba(211, 84, 0, 0.15)";
const CB_GREEN = "#2c8c5a";
const CB_RED = "#d35400";
const greenType = bullflowColorPref ? CB_GREEN : BULLFLOW_GREEN;
const redType = bullflowColorPref ? CB_RED : BULLFLOW_RED;

// Utility to classify topic if missing
const classifyTopic = (alert) => {
  if (alert.topic) return alert.topic;
  const name = (alert.filterName || '').toLowerCase();
  for (const topic of CHECKBOX_TOPICS) {
    if (name.includes(topic)) return topic;
  }
  return 'custom'; // default fallback
};

const playNotificationSoundAndSendAlert = (isSoundOn, alert) => {
  console.log("Attempting to play sound and send alert notification");
  try {
    if (isSoundOn) {
      const audio = new Audio(alertSound);
      audio.play().catch(e => console.log("Audio play failed:", e));
    } else {
      console.log("Sound is off, not playing alert sound");
    }
    const options = {
      body: `${convertOptionsSymbol(alert.symbol)} ${formatNumber(alert.alertPremium)}`,
      icon: './bullflow_3.png',
      badge: './bullflow_3.png',
    };
    if (!("Notification" in window)) {
      alert("This browser does not support desktop notification");
    } else if (Notification.permission === "granted") {
      new Notification(`Alert Triggered: ${alert.filterName}`, options);
    } else if (Notification.permission === "denied") {
      alert("You have blocked notifications. Please enable them in your browser settings if you wish to receive alerts.");
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          new Notification(`Bullflow alert: ${alert.filterName}`, options);
        }
      });
    }
  } catch (error) {
    console.log("Alert notif error");
  }
};

const StyledText = ({ text, optionColorShouldBeGreen, onClick }) => {
  const wrapperStyle = {
    borderRadius: '5px',
    color: '#fff',
    backgroundColor: optionColorShouldBeGreen ? BULLFLOW_OPACITY_GREEN : BULLFLOW_OPACITY_RED,
    borderColor: optionColorShouldBeGreen ? greenType : redType,
    borderWidth: '1px',
    borderStyle: 'solid',
    padding: '5px',
    lineHeight: '15px',
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  };

  return <span style={wrapperStyle} onClick={onClick}>{convertOptionsSymbol(text)}</span>;
};


function ListItem({ index, style, data }) {
  const item = data.items[index];
  const isDivider = item.isDivider;

  /* ────── Always call the hook (noop for divider rows) ────── */
  const { data: peakReturn, loading: peakLoading } = useAsyncPerformance(
    isDivider ? null : item.symbol,
    isDivider ? null : item.tradePrice,
    true,
    isDivider ? null : item.ms_of_day,
    isDivider ? null : item.timestamp,
    null
  );

  /* ────── Fade‑in state for the peak % text ────── */
  const [returnVisible, setReturnVisible] = useState(false);

  useEffect(() => {
    let timer;

    if (peakLoading) {
      // hide while loading
      setReturnVisible(false);
    } else {
      // show the value (including "N/A") once loading finishes
      timer = setTimeout(() => setReturnVisible(true), 0);
    }

    return () => clearTimeout(timer);
  }, [peakLoading, peakReturn]);


  /* ────── Divider row ────── */
  if (isDivider) {
    return (
      <div style={{ ...style, padding: '8px', boxSizing: 'border-box' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            color: '#666',
            fontSize: '12px',
            padding: '8px 0',
          }}
        >
          <div style={{ flex: 1, height: '1px', backgroundColor: '#333' }} />
          <span style={{ margin: '0 5px' }}>older</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#333' }} />
        </div>
      </div>
    );
  }

  /* ────── Helpers / render for normal row ────── */
  const optionInfo = parseOptionInfo(item.symbol);
  const shouldBeGreen = optionColorShouldBeGreenOnlyType(
    optionInfo['Option Type'],
  );
  const onListItemClicked = data.onListItemClicked;

  const formatReturn = (v) =>
    v === null || v === undefined || v === '' || Number.isNaN(Number(v))
      ? 'N/A'
      : `${Number(v).toFixed(1)}%`;
  const getReturnColor = (v) =>
    v == null ? '#aaa' : v >= 0 ? '#20d46b' : '#cc3753';

  /* Tooltip / icon helpers (unchanged) */
  const getTooltipText = (topic) => {
    if (topic === 'custom' || topic === '')
      return 'This trade matched a custom alert you created';
    if (topic === 'urgent')
      return 'Sequence of repeated, significant trades in a short timeframe.';
    if (topic === 'position_builder')
      return 'Large sequence of repeated, significant trades. Can cover a longer timespan.';
    if (topic === 'bullflow')
      return 'Sustained repeated, significant trades in a medium timeframe.';
    if (topic === 'sizable') return 'A single, large, unusual sweep trade.';
    if (topic === 'grenade') return 'A risky, short expiration, unusual sweep trade.';
    if (topic === 'discord')
      return "This is a trade entry/exit taken by Bullflow's veteran flow trader: Mike. Make sure you check the Discord to see his reasoning behind the trade.";
    return '';
  };

  const getIcon = (topic) => {
    switch (topic) {
      case 'custom':
      case '':
        return <Sparkles size={14} color="gray" />;
      case 'urgent':
        return <Zap size={14} color="gray" />;
      case 'position_builder':
        return <Layers size={14} color="gray" />;
      case 'bullflow':
        return <Activity size={14} color="gray" />;
      case 'sizable':
        return <Landmark size={14} color="gray" />;
      case 'grenade':
        return <AlertTriangle size={14} color="gray" />;
      case 'discord':
        return (
          <img
            src={DiscordIcon}
            alt="Discord icon"
            style={{ width: 14, height: 14 }}
          />
        );
      default:
        return <Info size={14} color="gray" />;
    }
  };

  /* ───────────────────────────── Render ───────────────────────────── */

  return (
    <div style={{ ...style, padding: '8px', boxSizing: 'border-box' }}>
      <div
        style={{
          border: 'none',
          borderRadius: 6,
          padding: '12px',
          backgroundColor: '#000',
          color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        {/* Title row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 550, marginRight: 6, fontSize: 14 }}>
              {item.filterName}
            </span>
            <Tooltip title={getTooltipText(item.topic)} placement="top">
              {getIcon(item.topic)}
            </Tooltip>
          </div>
          <span style={{ color: 'gray', fontSize: 12 }}>
            {timeAgo(item.timestamp)}
          </span>
        </div>

        {/* Peak return line */}
        <Tooltip
          title={`The highest % return this trade has reached since the alert`}
          placement="top">
          <div
            style={{
              color: '#aaa',
              fontSize: 12,
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            Peak Return:
            {peakLoading ? (
              <span style={{ fontStyle: 'italic' }}>…</span>
            ) : (
              <span
                style={{
                  color: getReturnColor(peakReturn),
                  opacity: returnVisible ? 1 : 0,
                  transition: 'opacity 0.4s ease',
                }}
              >
                {formatReturn(peakReturn)}
              </span>
            )}
          </div>
        </Tooltip>

        {/* Main content row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 12,
          }}
        >
          <StyledText
            text={item.symbol}
            optionColorShouldBeGreen={shouldBeGreen}
            onClick={() => onListItemClicked(item.symbol)}
          />
          <span
            style={{
              color: '#fff',
              width: 60,
              textAlign: 'right',
              marginLeft: 4,
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {item.alertPremium === -1
              ? ''
              : formatPremium(item.alertPremium, 1)}
          </span>
        </div>
      </div>
    </div >
  );
}


export default function AlertsScrollingVirtualizedList({
  displayed, onListItemClicked, userEmail,
  isSoundOn, handleAlertDelete
}) {
  /* ---------------- refs & state ---------------------------------------- */
  const emailAlertsRef = useRef([]);
  const systemAlertsRef = useRef([]);
  const [alerts, setAlerts] = useState([]);
  const [processedItems, setProcessedItems] = useState([]);
  const latestAlertIdRef = useRef(null);
  const tabVisibleRef = useRef(true);

  const [areNotificationsEnabled, setAreNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('desktopNotificationsEnabled', JSON.stringify(true));
    // console.log("Saved notifications enabled:", saved);
    return saved === null ? true : JSON.parse(saved);
  });

  const [filterOptions, setFilterOptions] = useState(() => {
    const saved = localStorage.getItem('alertFilterOptions');
    if (saved) return JSON.parse(saved);
    const init = {}; CHECKBOX_TOPICS.forEach(t => (init[t] = true));
    return init;
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const dropdownRef = useRef(null);

  /* ---------------- helpers -------------------------------------------- */
  const processAlertsWithDividers = (alerts) => {
    const items = [];
    for (let i = 0; i < alerts.length; i++) {
      items.push(alerts[i]);

      // If not the last alert, check time gap with next alert
      if (i < alerts.length - 1) {
        const currentTime = alerts[i].timestamp;
        const nextTime = alerts[i + 1].timestamp;
        const timeDiffHours = (currentTime - nextTime) / 3600; // Convert seconds to hours

        if (timeDiffHours > 8) {
          items.push({ isDivider: true, id: `divider-${alerts[i].id}` });
        }
      }
    }
    return items;
  };

  const mergeAndMaybeNotify = () => {
    const merged = [...emailAlertsRef.current];
    systemAlertsRef.current.forEach(a => {
      if (!merged.some(x => x.id === a.id)) merged.push(a);
    });

    const filtered = merged
      .filter(a => filterOptions[a.topic])
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, MAX_STORED_ALERTS);

    if (filtered.length && filtered[0].id !== latestAlertIdRef.current) {
      latestAlertIdRef.current = filtered[0].id;
      if (
        Date.now() - filtered[0].timestamp * 1000 < 30_000 &&
        areNotificationsEnabled
      ) {
        playNotificationSoundAndSendAlert(isSoundOn, filtered[0]);
      } else {
        // console.log("Alert is too old or notifications are disabled");
      }
    }

    if (tabVisibleRef.current) {
      setAlerts(filtered);
      setProcessedItems(processAlertsWithDividers(filtered));
    }
  };

  /* ---------------- firestore listeners (mounted once) ----------------- */
  useEffect(() => {
    if (!userEmail) return;

    const alertsRef = collection(firestore, '_flow_alerts');
    const qEmail = query(alertsRef,
      where('user', '==', userEmail),
      orderBy('timestamp', 'desc'),
      limit(100)
    );
    const qSys = query(alertsRef,
      where('isSystemAlert', '==', true),
      orderBy('timestamp', 'desc'),
      limit(100)
    );

    const unsubEmail = onSnapshot(qEmail, snap => {
      emailAlertsRef.current = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .map(a => ({ ...a, topic: classifyTopic(a) }))
        .filter(a => a.alertPremium != null);
      mergeAndMaybeNotify();
    });

    const unsubSys = onSnapshot(qSys, snap => {
      systemAlertsRef.current = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .map(a => ({ ...a, topic: classifyTopic(a) }))
        .filter(a => a.alertPremium != null);
      mergeAndMaybeNotify();
    });

    return () => { unsubEmail(); unsubSys(); };
  }, [userEmail, isSoundOn, areNotificationsEnabled]);

  /* ---------------- tab visibility ------------------------------------- */
  useEffect(() => {
    const handleVis = () => {
      tabVisibleRef.current = document.visibilityState === 'visible';
      if (tabVisibleRef.current) mergeAndMaybeNotify();
    };
    document.addEventListener('visibilitychange', handleVis);
    return () => document.removeEventListener('visibilitychange', handleVis);
  }, [areNotificationsEnabled]);

  /* ---------------- persist filter selection --------------------------- */
  useEffect(() => {
    localStorage.setItem('alertFilterOptions', JSON.stringify(filterOptions));
    mergeAndMaybeNotify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions, areNotificationsEnabled]);

  /* ---------------- click-outside to close dropdown -------------------- */
  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ---------------- render guard --------------------------------------- */
  if (!displayed) return null;

  /* ---------------- render --------------------------------------------- */
  const itemData = { items: processedItems, onListItemClicked };

  return (
    <div style={{ width: '100%', height: '100%', padding: '4px 8px', boxSizing: 'border-box' }}>
      {/* - Header / Filters - */}
      <div style={{ padding: '12px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontWeight: 'bold', color: '#fff' }}>Algo and Custom Alerts</h2>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CustomCircleIconButton
              onClick={() => {
                setAreNotificationsEnabled(prev => {
                  const next = !prev;
                  localStorage.setItem('desktopNotificationsEnabled', JSON.stringify(next));
                  return next;
                });
              }}
              Icon={areNotificationsEnabled ? Bell : BellOff}
              tooltipContent={areNotificationsEnabled
                ? 'Disable desktop notifications'
                : 'Enable desktop notifications'
              }
              iconSize={14}
              buttonPadding="8px"
              textSize={14}
              marginRight="8px"
            />
            <CustomCircleIconButton
              onClick={() => setIsEditing(p => !p)}
              Icon={isEditing ? ArrowLeft : PencilLine}
              tooltipContent={isEditing ? 'Return to alerts' : 'Edit alerts'}
              iconSize={14} buttonPadding="8px" textSize={14} marginRight="4px"
            />
            <div style={{ position: 'relative', marginRight: '8px' }} ref={dropdownRef}>
              <CustomCircleIconButton
                onClick={() => setShowDropdown(p => !p)}
                Icon={SlidersHorizontal}
                tooltipContent="Filter alerts by topic"
                iconSize={14} buttonPadding="8px" textSize={14}
              />
              {showDropdown && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  right: 0,
                  backgroundColor: '#2c2c2c',
                  color: '#ccc',
                  borderRadius: '6px',
                  padding: '8px',
                  zIndex: 1000
                }}>
                  {CHECKBOX_TOPICS.map(topic => (
                    <label key={topic} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      <input
                        type="checkbox"
                        checked={filterOptions[topic]}
                        onChange={() => setFilterOptions(prev => ({ ...prev, [topic]: !prev[topic] }))}
                        style={{ marginRight: '4px' }}
                      />
                      {topic[0].toUpperCase() + topic.slice(1)}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* <p style={{ margin: 0, marginTop: '4px', color: '#ccc', fontSize: '14px' }}>
          {isEditing
            ? 'View and delete your alerts'
            : 'Bullflow algo alerts and your custom alerts will appear here.'}
        </p> */}
      </div>

      {/* - Content - */}
      {isEditing ? (
        <AlertsListView email={userEmail || ''} deleteAlert={handleAlertDelete} />
      ) : (
        <AutoSizer>
          {({ width, height }) => (
            <List
              width={width} height={height}
              itemCount={processedItems.length} itemSize={110} itemData={itemData}
            >
              {ListItem}
            </List>
          )}
        </AutoSizer>
      )}
    </div>
  );
}